import { useCallback, useContext } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { RoomContext, RoomContextType } from '../RoomContext'
import { sleep } from '../utils'
import ElectronIPC, { WindowMetrics } from './ElectronIPC'
import useRoomContextRef from './useRoomContextRef'
import * as Constants from '../services/Constants'
import useAppContextRef from './useAppContextRef'
import { env } from './EnvironmentVariables'
import { AnalyticsEvents, trackPageVisit } from './AnalyticsService'
import useAnalytics from './useAnalytics'
import { logger as rootLogger } from '../services/Logger'
import _ from 'lodash'
import { SettingsTab } from '@/components/SessionSettings/SessionSettings'

export type SetRetractOptions = {
    doNotRestoreSavedWindowDimension?: boolean
    noTransitionAnimation?: boolean
}

const log = rootLogger.createChildLogger({ name: 'MiscRoomActions' })

export default function useMiscRoomActions() {
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { appStateRef } = useAppContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { logUserActivityWithContext } = useAnalytics()

    const windowRetract = useCallback(
        async (options?: { doNotRestoreSavedWindowDimension: boolean }) => {
            if (!env.IS_ELECTRON) {
                return
            }
            log.info('windowRetract', 'windowRetract start.')

            const windowTitleBarNext = {
                mainWindowShowMinimize: true,
                mainWindowShowMaximize: true,
                mainWindowShowClose: true,
            }

            if (!_.isEqual(appStateRef.current.windowTitleBar, windowTitleBarNext)) {
                appDispatch({
                    type: 'update_window_title_bar',
                    value: windowTitleBarNext,
                })
            }

            if (!options?.doNotRestoreSavedWindowDimension) {
                const j = localStorage.getItem('CollaboratePage.winmetrics.retracted')
                try {
                    if (j == null) {
                        throw new Error('null')
                    }

                    const wmToRestore: WindowMetrics = JSON.parse(j)
                    if (wmToRestore.width > Constants.collaborate_page_retracted_max_width) {
                        wmToRestore.width = Constants.collaborate_page_retracted_default_width
                    }
                    if (wmToRestore.width < Constants.collaborate_page_retracted_default_width) {
                        wmToRestore.width = Constants.collaborate_page_retracted_default_width
                    }
                    console.log(`windowRetract: Calling setWindowBounds ` + JSON.stringify(wmToRestore))

                    ElectronIPC.setWindowBounds({
                        x: wmToRestore.x,
                        y: wmToRestore.y,
                        width: wmToRestore.width,
                        height: wmToRestore.height,
                        isMaximized: false,
                    })
                } catch (e) {
                    console.error(e)
                    ElectronIPC.setWindowBounds({
                        width: Constants.collaborate_page_retracted_default_width,
                        isMaximized: false,
                        constraintX: 'right',
                    })
                }
            }

            const tSetWindowBoundsCompleteMs = 250
            setTimeout(() => {
                log.verbose('windowRetract', 'windowRetract setting isRetractedInProgress to false.')
                roomDispatch({
                    type: 'update_room_context',
                    value: { isRetractedInProgress: false },
                })
            }, tSetWindowBoundsCompleteMs)
        },
        [appDispatch]
    )

    const windowRevert = useCallback(
        async (options?: { doNotRestoreSavedWindowDimension: boolean }) => {
            log.info('windowRevert', 'windowRevert start.')

            const windowTitleBarNext = {
                mainWindowShowMinimize: true,
                mainWindowShowMaximize: true,
                mainWindowShowClose: true,
            }

            if (!_.isEqual(appStateRef.current.windowTitleBar, windowTitleBarNext)) {
                appDispatch({
                    type: 'update_window_title_bar',
                    value: windowTitleBarNext,
                })
            }

            if (!options?.doNotRestoreSavedWindowDimension) {
                const j = localStorage.getItem('CollaboratePage.winmetrics.normal')
                try {
                    if (j == null) {
                        throw new Error('null')
                    }

                    const wmToRestore: WindowMetrics = JSON.parse(j)
                    if (wmToRestore.width < Constants.collaborate_page_normal_min_width) {
                        wmToRestore.width = Constants.collaborate_page_normal_default_width
                    }
                    console.log(`windowRevert: Calling setWindowBounds ` + JSON.stringify(wmToRestore))
                    ElectronIPC.setWindowBounds({
                        x: wmToRestore.x,
                        y: wmToRestore.y,
                        width: wmToRestore.width,
                        height: wmToRestore.height,
                        isMaximized: wmToRestore.isMaximized,
                    })
                } catch (e) {
                    localStorage.removeItem('CollaboratePage.winmetrics.normal')
                    ElectronIPC.setWindowBounds({
                        width: Constants.collaborate_page_normal_default_width,
                        isMaximized: false,
                        centerX: true,
                        centerY: true,
                    })
                }
            }

            const tSetWindowBoundsCompleteMs = 250
            setTimeout(() => {
                log.verbose('windowRevert', 'windowRevert setting isRetractedInProgress to false.')
                roomDispatch({
                    type: 'update_room_context',
                    value: { isRetractedInProgress: false },
                })
            }, tSetWindowBoundsCompleteMs)
        },
        [appDispatch]
    )

    const setRetract = useCallback(
        (isRetracted: boolean, source: string, options?: SetRetractOptions) => {
            const tWaitCssApplyMs = 50
            const tWaitWindowResize = 150

            log.info('setRetract', 'start.', { isRetracted, source, options })

            if (!options?.noTransitionAnimation) {
                const element = document.getElementById('CollaboratePage_root')
                if (element) {
                    element.style.opacity = '0'
                    element.style.transition = 'opacity 0s'
                    element.offsetHeight
                }
                setTimeout(() => {
                    const element = document.getElementById('CollaboratePage_root')
                    if (element) {
                        element.style.opacity = '1'
                        element.style.transition = 'opacity 0.05s ease-in'
                    }
                }, tWaitWindowResize)
            }

            // wait for css to get applied
            const _setTimeout = options?.noTransitionAnimation ? (f: any) => f() : setTimeout

            _setTimeout(() => {
                console.log(`setRetract: setting isRetracted=${isRetracted}; event source="${source}".`)
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        isRetracted: isRetracted,
                        isRetractedPrev: roomStateRef.current.isRetracted,
                        isRetractedDoNotRestoreSavedWindowDimension: options?.doNotRestoreSavedWindowDimension || false,
                        isRetractedInProgress: true,
                    },
                    caller: 'setRetract',
                })
            }, tWaitCssApplyMs)

            // implicit sticky window when GalleryView is retracted.
            if (env.STICKY_WINDOW_WHEN_GALLERY_RETRACTED) {
                if (isRetracted && !roomStateRef.current.isWindowSticky) {
                    ElectronIPC.setWindowOptions({ isAlwaysOnTop: true })
                } else if (!isRetracted && !roomStateRef.current.isWindowSticky) {
                    ElectronIPC.setWindowOptions({ isAlwaysOnTop: false })
                }
            }
        },
        [roomDispatch]
    )

    const toggleRetract = useCallback(() => {
        setRetract(!roomStateRef.current.isRetracted, 'user_interaction')
    }, [roomStateRef, setRetract])

    const toggleSidebarRetract = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isSidebarRetracted: !roomStateRef.current.isSidebarRetracted },
        })
    }, [roomDispatch, roomStateRef])

    const toggleWindowSticky = useCallback(() => {
        const isWindowStickyNext = !roomStateRef.current.isWindowSticky
        ElectronIPC.setWindowOptions({ isAlwaysOnTop: isWindowStickyNext })

        roomDispatch({
            type: 'update_room_context',
            value: { isWindowSticky: isWindowStickyNext },
        })
    }, [roomDispatch, roomStateRef])

    const enterFullscreen = useCallback(
        async (triggerSource: 'os' | 'internal') => {
            if (!env.IS_ELECTRON) {
                return
            }

            if (appStateRef.current?.appState?.isFullscreen) {
                return
            }

            let wasRetractedBeforeFullscreen = roomStateRef.current.isRetracted
            const isRetractedInProgress = roomStateRef.current.isRetractedInProgress
            if (isRetractedInProgress) {
                wasRetractedBeforeFullscreen = roomStateRef.current.isRetractedPrev
            }
            log.info('enterFullscreen', 'start.', {
                isRetractedInProgress,
                wasRetractedBeforeFullscreen,
            })

            if (triggerSource === 'internal') {
                if (env.OS === 'darwin') {
                    // Exit fullscreen that might have been there.
                    // The point is that, if native fullscreen is already on, commanding another fullscreen
                    // on top results in strange window behavior (Mac). This is rarely a problem since native
                    // fullscreen gets picked up by the external event module and triggers enterFullscreen('os')
                    // and internal `enterFullscreen('internal')` double-spend is restricted programmatically.
                    ElectronIPC.setWindowBounds({
                        isFullscreen: false,
                    })

                    await sleep(100)
                }

                // if app not focused, then trigger focus (fixes #985)
                const windowState = await ElectronIPC.getWindowState({ windowName: 'mainWindow' })
                if (!windowState?.isFocused) {
                    ElectronIPC.setWindowOptions({ triggerFocus: true })
                    await sleep(1000)
                }

                // Enter fullscreen mode
                ElectronIPC.setWindowBounds({
                    isFullscreen: true,
                })
            }

            appDispatch({
                type: 'update_app_state',
                value: {
                    isFullscreen: true,
                    // save this state so that we can restore the proper isRetracted
                    // state on exit full screen.
                    wasRetractedBeforeFullscreen,
                },
            })

            roomDispatch({
                type: 'update_room_context',
                value: { isSidebarDetached: true },
            })

            setTimeout(() => {
                ElectronIPC.setWindowOptions({ triggerFocus: true })
            }, 1)
        },
        [appDispatch, roomDispatch, appStateRef]
    )

    const exitFullscreen = useCallback(() => {
        if (!env.IS_ELECTRON) {
            return
        }

        if (!appStateRef.current.appState.isFullscreen) {
            return
        }

        const wasRetractedBeforeFullscreen = appStateRef.current.appState.wasRetractedBeforeFullscreen
        log.info('exitFullscreen', 'start.', {
            wasRetractedBeforeFullscreen,
        })

        ElectronIPC.setWindowBounds({
            isFullscreen: false,
        })
        appDispatch({
            type: 'update_app_state',
            value: { isFullscreen: false },
        })
        roomDispatch({
            type: 'update_room_context',
            value: {
                isSidebarDetached: false,
                isRetracted: wasRetractedBeforeFullscreen,
            },
        })

        if (roomStateRef.current.isSidebarRetracted) {
            roomDispatch({
                type: 'update_room_context',
                value: { isSidebarRetracted: false },
            })
        }
    }, [appDispatch, roomDispatch, roomStateRef])

    const toggleFullscreen = useCallback(() => {
        if (appStateRef.current?.appState?.isFullscreen) {
            exitFullscreen()
            logUserActivityWithContext(AnalyticsEvents.CLICKED_EXIT_FULLSCREEN)
        } else {
            enterFullscreen('internal')
            logUserActivityWithContext(AnalyticsEvents.CLICKED_ENTER_FULLSCREEN)
        }
    }, [appStateRef, enterFullscreen, exitFullscreen])

    const openSettingsModal = (page?: SettingsTab, section?: string) => {
        const open = (page: SettingsTab | null | undefined) => {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isSettingModalOpen: {
                        tab: page ?? 'audio',
                        section,
                    },
                },
            })
        }

        page = page || 'audio'

        trackPageVisit(page)
        open(page)
        // show modal to front.
        ElectronIPC.setWindowOptions({
            windowName: 'SessionSettingsModal',
            triggerFocus: true,
        })
    }

    const closeSettingsModal = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isSettingModalOpen: null },
        })
    }, [roomDispatch])

    const setShowWaitingRoomPopup = useCallback(
        (show: boolean) => {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isForceShowingWaitingRoomPopup: show,
                    // explicitly set to false only when show === false.
                    ...(show === false ? { isShowingWaitingRoomPopup: false } : {}),
                },
            })
        },
        [roomDispatch]
    )

    const windowThinStripRight = useCallback(() => {
        // Resize window to a thin strip at the RHS corner of the screen,
        // while removing the top status bar, since the buttons there
        // occupy too much space.
        ElectronIPC.setWindowBounds({
            width: Constants.collaborate_page_full_retracted_default_dim.width,
            height: Constants.collaborate_page_full_retracted_default_dim.height,
            constraintX: 'right',
            allowSmallWidth: true,
        })

        appDispatch({
            type: 'update_window_title_bar',
            value: {
                mainWindowShowMinimize: false,
                mainWindowShowMaximize: false,
                mainWindowShowClose: false,
            },
        })
    }, [appDispatch])

    const windowThinStripRightForFullscreen = useCallback(() => {
        ElectronIPC.setWindowBounds({
            windowName: 'SidebarModal',
            width: Constants.collaborate_page_full_retracted_default_dim.width,
            height: Constants.collaborate_page_full_retracted_default_dim.height,
            constraintX: 'right',
            allowSmallWidth: true,
        })
    }, [])

    const windowThinStripRevertForFullscreen = useCallback(() => {
        ElectronIPC.setWindowBounds({
            windowName: 'SidebarModal',
            width: 320,
            height: 800,
            constraintX: 'right',
        })
    }, [])

    const openRoomWaitlistDialog = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isRoomWaitlistDialogOpen: true },
        })
    }, [roomDispatch])

    const closeRoomWaitlistDialog = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isRoomWaitlistDialogOpen: false },
        })
    }, [roomDispatch])

    const openWaitingRoomDialog = () => {
        if (roomStateRef.current.roomWaitlist.length > 0) {
            roomDispatch({
                type: 'update_room_context',
                value: { isRoomWaitlistDialogOpen: true },
            })
        }
    }

    const unhideAllVidoesFromGallery = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { videoHiddenSessionIds: [] },
        })
    }, [])

    return {
        toggleRetract,
        setRetract,
        toggleSidebarRetract,
        toggleWindowSticky,
        toggleFullscreen,
        openSettingsModal,
        closeSettingsModal,
        setShowWaitingRoomPopup,
        openRoomWaitlistDialog,
        closeRoomWaitlistDialog,
        openWaitingRoomDialog,
        unhideAllVidoesFromGallery,

        enterFullscreen,
        exitFullscreen,
        windowRetract,
        windowRevert,
        windowThinStripRight,
        windowThinStripRightForFullscreen,
        windowThinStripRevertForFullscreen,
    }
}
